/**
 * Diálogo de categoria com as regras do app (app/categories/[id].tsx + categoryRepository): nome
 * obrigatório, tipo só na criação, cor e ícone do catálogo do app, subcategorias com nome único
 * dentro da categoria (renomear, reordenar, remover — remover deixa os lançamentos sem subcategoria),
 * arquivar/desarquivar (categoria de sistema não arquiva), mesclar (tudo vai pro destino do mesmo
 * tipo) e apagar (os lançamentos vão pra "Outros" ou outra ativa do mesmo tipo; sem destino, não apaga).
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import type { Model } from '../viewer/lib/model';
import type { CategoryRow, SubcategoryRow } from '../viewer/lib/types';
import type { CategoriesEditor, SubcategoryDraft } from '../viewer/lib/editing';
import { CategoryIcon } from '../viewer/components/CategoryIcon';
import { catChip } from '../viewer/components/sheet';
import { normalize, plural } from '../viewer/lib/text';
import { Foot, useBusy, type Done } from './AccountDialogs';
import { Modal, field, label } from './ui';
import { PRESET_COLORS, Seg } from './parts';

/** O catálogo de ícones do app (lib/icons.ts), seção por seção, com os rótulos em português. */
export const CATEGORY_ICON_GROUPS: Array<{ label: string; icons: string[] }> = [
  { label: 'Dinheiro', icons: ['wallet', 'bank', 'piggy-bank', 'credit-card', 'money', 'currency-dollar', 'coins', 'hand-coins', 'hand-withdraw', 'vault', 'invoice', 'receipt', 'scales', 'percent', 'trend-up', 'chart-line-up', 'chart-pie', 'chart-donut', 'arrows-left-right', 'repeat', 'tag', 'barcode', 'qr-code', 'currency-btc'] },
  { label: 'Casa & Contas', icons: ['house', 'buildings', 'couch', 'bed', 'lightbulb', 'plug', 'drop', 'flame', 'lightning', 'wifi-high', 'television', 'washing-machine', 'toilet-paper', 'broom', 'trash', 'hammer', 'wrench', 'paint-roller', 'key', 'shield', 'umbrella', 'bathtub'] },
  { label: 'Comida & Bebida', icons: ['fork-knife', 'cooking-pot', 'pizza', 'hamburger', 'ice-cream', 'cake', 'coffee', 'wine', 'beer', 'martini', 'bowl-food', 'basket'] },
  { label: 'Transporte', icons: ['car', 'taxi', 'bus', 'train', 'airplane', 'motorcycle', 'bicycle', 'gas-pump', 'map-pin', 'compass', 'map-trifold'] },
  { label: 'Compras', icons: ['shopping-cart', 'shopping-bag', 'storefront', 'package', 'gift', 't-shirt', 'dress', 'sneaker', 'watch', 'sunglasses', 'handbag', 'scissors', 'crown'] },
  { label: 'Saúde & Bem-estar', icons: ['heart', 'heartbeat', 'first-aid', 'pill', 'syringe', 'stethoscope', 'tooth', 'eyeglasses', 'brain', 'barbell', 'sparkle'] },
  { label: 'Educação & Trabalho', icons: ['graduation-cap', 'book-open', 'books', 'certificate', 'pen-nib', 'presentation', 'calculator', 'briefcase', 'laptop', 'device-mobile', 'printer', 'notebook', 'file-text', 'gear'] },
  { label: 'Lazer & Hobbies', icons: ['game-controller', 'film-slate', 'music-note', 'microphone', 'headphones', 'palette', 'camera', 'robot', 'ticket', 'soccer-ball', 'basketball', 'trophy', 'medal', 'puzzle-piece', 'star'] },
  { label: 'IA & Streaming', icons: ['magic-wand', 'cpu', 'atom', 'openai', 'chat-dots', 'monitor-play', 'popcorn', 'broadcast', 'playlist', 'spotify', 'youtube', 'twitch', 'tiktok'] },
  { label: 'Viagem & Natureza', icons: ['suitcase-rolling', 'tent', 'mountains', 'tree', 'leaf', 'flower-lotus', 'sun', 'moon', 'cloud', 'cloud-rain', 'snowflake'] },
  { label: 'Família & Pets', icons: ['user', 'hand-heart', 'baby', 'paw-print', 'dog', 'cat', 'bird', 'fish', 'bone', 'confetti', 'balloon'] },
  { label: 'Geral', icons: ['calendar', 'clock', 'bell', 'phone', 'cloud-arrow-up', 'plus-circle', 'dots-three', 'flag', 'target', 'church', 'cigarette'] },
];

type View = 'form' | 'merge' | 'delete';
type Draft = SubcategoryDraft & { key: number };
type Kind = 'expense' | 'income';

export function CategoryDialog({ model, category, subcategories = [], defaultType = 'expense', editor, onClose, onDone, onCreated, onGone }: {
  model: Model;
  /** sem categoria = criar */
  category?: CategoryRow;
  /** as subcategorias ativas dela, na ordem do app */
  subcategories?: SubcategoryRow[];
  defaultType?: Kind;
  editor: CategoriesEditor;
  onClose: () => void;
  onDone: Done;
  /** categoria nova: id e tipo, pra tela já abrir nela */
  onCreated?: (id: string, type: Kind) => void;
  /** mesclada ou apagada: pra onde os lançamentos foram (null quando não dá pra saber) */
  onGone?: (destinationId: string | null) => void;
}) {
  const editing = !!category;
  const [view, setView] = useState<View>('form');
  const [name, setName] = useState(category?.name ?? '');
  const [type, setType] = useState<Kind>(category?.type ?? defaultType);
  const [color, setColor] = useState(category?.color ?? PRESET_COLORS[0]);
  const [icon, setIcon] = useState(category?.icon ?? 'tag');
  const [subs, setSubs] = useState<Draft[]>(() => subcategories.map((s, i) => ({ id: s.id, name: s.name, key: i })));
  const [newSub, setNewSub] = useState('');
  const [iconQuery, setIconQuery] = useState('');
  const [dest, setDest] = useState('');
  const [fallback, setFallback] = useState<string | null | undefined>(undefined);
  const { busy, error, setError, run } = useBusy();
  const nextKey = useRef(subcategories.length);

  const countOf = useMemo(() => {
    const m = new Map<string, number>();
    for (const t of model.transactions) if (!t.isProjected && t.categoryId) m.set(t.categoryId, (m.get(t.categoryId) ?? 0) + 1);
    return (id: string) => m.get(id) ?? 0;
  }, [model]);
  const txCount = category ? countOf(category.id) : 0;
  const peers = useMemo(() => [...model.categories.values()].filter(c => c.type === type && !c.isArchived && c.id !== category?.id).sort((a, b) => a.name.localeCompare(b.name, 'pt-BR')), [model, type, category]);
  const alone = editing && peers.length === 0;
  const removed = subcategories.filter(s => !subs.some(d => d.id === s.id));
  const colors = PRESET_COLORS.includes(color) ? PRESET_COLORS : [color, ...PRESET_COLORS];
  const nq = normalize(iconQuery.trim());
  const groups = CATEGORY_ICON_GROUPS.map(g => ({ ...g, icons: nq ? g.icons.filter(i => normalize(i).includes(nq) || normalize(g.label).includes(nq)) : g.icons })).filter(g => g.icons.length);

  // O destino de quem é apagada vem do banco (é regra do app: "Outros" do seed, senão a primeira do tipo).
  useEffect(() => {
    if (view !== 'delete' || !category) return;
    let alive = true;
    setFallback(undefined);
    void editor.fallbackName(category.id).then(n => { if (alive) setFallback(n); });
    return () => { alive = false; };
  }, [view, category, editor]);

  const addSub = () => {
    const v = newSub.trim();
    if (!v) return;
    setSubs([...subs, { id: null, name: v, key: nextKey.current++ }]);
    setNewSub('');
  };
  const move = (i: number, dir: -1 | 1) => {
    const j = i + dir;
    if (j < 0 || j >= subs.length) return;
    const a = [...subs];
    [a[i], a[j]] = [a[j], a[i]];
    setSubs(a);
  };
  const submit = () => {
    const n = name.trim();
    if (!n) return setError('Dê um nome à categoria.');
    const list: SubcategoryDraft[] = [...subs.map(s => ({ id: s.id, name: s.name.trim() })), ...(newSub.trim() ? [{ id: null, name: newSub.trim() }] : [])].filter(s => s.name);
    const seen = new Set<string>();
    for (const s of list) {
      const k = normalize(s.name);
      if (seen.has(k)) return setError(`"${s.name}" aparece duas vezes. Cada subcategoria tem um nome só.`);
      seen.add(k);
    }
    void run(async () => {
      if (editing) { await editor.update(category!.id, { name: n, type, icon, color, subcategories: list }); onDone(`${n} atualizada`); }
      else { const id = await editor.create({ name: n, type, icon, color, subcategories: list }); onCreated?.(id, type); onDone(`Categoria ${n} criada`); }
      onClose();
    });
  };
  const archive = () => void run(async () => {
    const c = category!;
    if (c.isArchived) { await editor.unarchive(c.id); onDone(`${c.name} voltou a aparecer nas listas`, () => editor.archive(c.id)); }
    else { await editor.archive(c.id); onDone(`${c.name} arquivada · os lançamentos continuam`, () => editor.unarchive(c.id)); }
    onClose();
  });
  const destCat = peers.find(p => p.id === dest);
  const doMerge = () => {
    if (!category || !destCat) return;
    void run(async () => {
      const n = await editor.merge(category.id, destCat.id);
      onGone?.(destCat.id);
      onDone(`${plural(n, 'lançamento movido', 'lançamentos movidos')} de ${category.name} para ${destCat.name}`);
      onClose();
    });
  };
  const doDelete = () => {
    if (!category) return;
    void run(async () => {
      const n = await editor.remove(category.id);
      onGone?.(peers.find(p => p.name === fallback)?.id ?? null);
      onDone(`${category.name} apagada${n ? ` · ${plural(n, 'lançamento foi', 'lançamentos foram')} para ${fallback ?? 'outra categoria'}` : ''}`);
      onClose();
    });
  };

  const title = view === 'merge' ? `Mesclar ${category!.name}` : view === 'delete' ? `Apagar ${category!.name}` : editing ? 'Editar categoria' : 'Nova categoria';
  const tint = { color, background: `color-mix(in srgb, ${color} 14%, transparent)` };
  return (
    <Modal title={title} onClose={onClose} testId="category-form">
      <div className="acc-dlg cat-dlg">
        {view === 'form' && (
          <>
            <div className="cat-dlg-name">
              <span className="cat-dlg-preview" style={tint} aria-hidden="true"><CategoryIcon name={icon} size={20} /></span>
              <label className="acc-dlg-group is-grow"><span className={label}>Nome da categoria</span><input className={field} placeholder="Ex.: Alimentação" value={name} onChange={e => setName(e.target.value)} autoFocus data-testid="category-name" /></label>
            </div>
            {!editing && <div className="acc-dlg-group"><span className={label}>Tipo</span><Seg options={[{ value: 'expense' as const, label: 'Despesa' }, { value: 'income' as const, label: 'Receita' }]} value={type} onChange={setType} testId="cat-type" /></div>}
            <div className="acc-dlg-group">
              <span className={label}>Cor</span>
              <div className="pick-colors">
                {colors.map((c, i) => <button key={c} type="button" aria-label={`Cor ${i + 1}`} aria-pressed={color === c} className={color === c ? 'is-on' : ''} style={{ background: c }} onClick={() => setColor(c)} />)}
              </div>
            </div>
            <div className="acc-dlg-group">
              <div className="cat-dlg-icons-head">
                <span className={label}>Ícone</span>
                <label className="sheet-search"><CategoryIcon name="magnifying-glass" size={14} /><input value={iconQuery} onChange={e => setIconQuery(e.target.value)} placeholder="Buscar ícone…" aria-label="Buscar ícone" /></label>
              </div>
              <div className="cat-dlg-icons" data-testid="cat-icons">
                {groups.map(g => (
                  <div key={g.label} className="cat-dlg-icon-group">
                    <span>{g.label}</span>
                    <div className="pick-icons">
                      {g.icons.map(i => (
                        <button key={i} type="button" aria-label={i} title={i} aria-pressed={icon === i} className={icon === i ? 'is-on' : ''} style={icon === i ? { ...tint, borderColor: color } : undefined} onClick={() => setIcon(i)}>
                          <CategoryIcon name={i} size={17} />
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
                {groups.length === 0 && <span className="acc-dlg-note">Nenhum ícone com esse nome.</span>}
              </div>
            </div>
            <div className="acc-dlg-group">
              <span className={label}>Subcategorias</span>
              <div className="cat-dlg-subs" data-testid="cat-subs">
                {subs.map((s, i) => (
                  <div key={s.key} className="cat-dlg-sub">
                    <input className={field} value={s.name} onChange={e => setSubs(subs.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))} aria-label={`Subcategoria ${i + 1}`} data-testid="cat-sub-name" />
                    <button type="button" aria-label={`Mover ${s.name} para cima`} disabled={i === 0} onClick={() => move(i, -1)}><CategoryIcon name="caret-up" size={14} /></button>
                    <button type="button" aria-label={`Mover ${s.name} para baixo`} disabled={i === subs.length - 1} onClick={() => move(i, 1)}><CategoryIcon name="caret-down" size={14} /></button>
                    <button type="button" className="is-danger" aria-label={`Remover ${s.name}`} onClick={() => setSubs(subs.filter((_, j) => j !== i))} data-testid="cat-sub-remove">✕</button>
                  </div>
                ))}
                <div className="cat-dlg-sub is-new">
                  <input className={field} placeholder="Nova subcategoria…" value={newSub} onChange={e => setNewSub(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addSub(); } }} aria-label="Nova subcategoria" data-testid="cat-sub-new" />
                  <button type="button" className="acc-btn" onClick={addSub} data-testid="cat-sub-add">Adicionar</button>
                </div>
              </div>
              {removed.length > 0 && <span className="acc-dlg-note text-warn" data-testid="cat-removed-note">Ao salvar, os lançamentos de {removed.map(s => s.name).join(', ')} ficam sem subcategoria.</span>}
            </div>
            {editing && (
              <div className="cat-dlg-actions">
                {!category!.isSystem && <button type="button" onClick={archive} disabled={busy} data-testid="cat-archive">{category!.isArchived ? 'Desarquivar categoria' : 'Arquivar categoria'}</button>}
                <button type="button" onClick={() => { setError(''); setView('merge'); }} disabled={busy || alone} title={alone ? 'Precisa de outra categoria ativa do mesmo tipo' : undefined} data-testid="cat-merge">Mesclar com outra categoria</button>
                <button type="button" className="is-danger" onClick={() => { setError(''); setView('delete'); }} disabled={busy || alone} title={alone ? 'Precisa de outra categoria ativa do mesmo tipo pra receber os lançamentos' : undefined} data-testid="cat-delete">Apagar categoria</button>
              </div>
            )}
            <Foot busy={busy} error={error} submitLabel={editing ? 'Salvar' : 'Criar categoria'} onCancel={onClose} onSubmit={submit} />
          </>
        )}
        {view === 'merge' && category && (
          <>
            <p className="acc-dlg-note">Todos os lançamentos e subcategorias de <b>{category.name}</b> ({plural(txCount, 'lançamento', 'lançamentos')}) vão para a categoria que você escolher.</p>
            <div className="cat-dlg-dests" role="group" aria-label="Categoria de destino">
              {peers.map(p => (
                <button key={p.id} type="button" aria-pressed={dest === p.id} className={`acc-nature ${dest === p.id ? 'is-on' : ''}`} onClick={() => setDest(p.id)} data-testid="cat-dest">
                  {catChip(p.color, p.icon)}<span>{p.name}</span><small>{plural(countOf(p.id), 'lançamento', 'lançamentos')}</small>
                </button>
              ))}
            </div>
            <p className="cat-dlg-warn">Esta ação não pode ser desfeita.</p>
            {error && <p role="alert" className="acc-dlg-error" data-testid="dialog-error">{error}</p>}
            <div className="acc-dlg-foot">
              <button type="button" className="acc-btn" onClick={() => setView('form')} disabled={busy}>Voltar</button>
              <button type="button" className="acc-btn is-primary" onClick={doMerge} disabled={busy || !destCat} data-testid="dialog-submit">{busy ? 'Mesclando…' : destCat ? `Mesclar com ${destCat.name}` : 'Escolha o destino'}</button>
            </div>
          </>
        )}
        {view === 'delete' && category && (
          <>
            {fallback !== undefined && (
              <p className="acc-dlg-note" data-testid="cat-delete-text">
                {fallback
                  ? txCount > 0
                    ? <>"{category.name}" tem {plural(txCount, 'lançamento', 'lançamentos')}. Ao apagar, eles serão movidos para "{fallback}" e ficam sem subcategoria.</>
                    : <>Apagar "{category.name}"? As subcategorias dela somem junto.</>
                  : <>Não há outra categoria ativa de {type === 'income' ? 'receita' : 'despesa'} pra receber os lançamentos. Crie (ou desarquive) uma antes de apagar esta.</>}
              </p>
            )}
            <p className="cat-dlg-warn">Esta ação não pode ser desfeita.</p>
            {error && <p role="alert" className="acc-dlg-error" data-testid="dialog-error">{error}</p>}
            <div className="acc-dlg-foot">
              <button type="button" className="acc-btn" onClick={() => setView('form')} disabled={busy}>Voltar</button>
              <button type="button" className="acc-btn is-primary is-delete" onClick={doDelete} disabled={busy || fallback === undefined || fallback === null} data-testid="dialog-submit">{busy ? 'Apagando…' : 'Apagar categoria'}</button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}
